import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MessageSquare, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import {
  reachCommsAPI, ReachCommsItem, ApprovalStatus, ReachStage
} from '../../services/reachService';
import { REACHStageBadge } from '../../components/reach/common/REACHStageBadge';

const STAGES: ReachStage[] = ['R', 'E', 'A', 'C', 'H'];
const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};
const APPROVAL_COLORS: Record<ApprovalStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const EMPTY: Omit<ReachCommsItem, 'id' | 'createdAt' | 'createdBy'> = {
  item: '', audience: '', draftedBy: '', approver: '',
  approvalStatus: 'pending', sendDate: '', channel: '', notes: '', reachStage: 'R',
};

type FormData = Omit<ReachCommsItem, 'id' | 'createdAt' | 'createdBy'>;

interface FormModalProps {
  initial?: ReachCommsItem | null;
  onSave: (data: FormData) => Promise<void>;
  onClose: () => void;
}

const FormModal: React.FC<FormModalProps> = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState<FormData>(initial ? {
    item: initial.item, audience: initial.audience, draftedBy: initial.draftedBy,
    approver: initial.approver, approvalStatus: initial.approvalStatus,
    sendDate: initial.sendDate, channel: initial.channel, notes: initial.notes ?? '',
    reachStage: initial.reachStage,
  } : { ...EMPTY });
  const [saving, setSaving] = useState(false);

  const set = (k: keyof FormData, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.item.trim() || !form.sendDate) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
          <h2 className="text-base font-bold text-navy">{initial ? 'Edit Comms Item' : 'New Comms Item'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-navy"><X size={18} /></button>
        </div>
        <div className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Communication item <span className="text-red-500">*</span></label>
            <textarea rows={2} value={form.item} onChange={e => set('item', e.target.value)}
              placeholder="e.g. Launch communication from sponsor"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Audience</label>
              <input value={form.audience} onChange={e => set('audience', e.target.value)} placeholder="All staff"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Channel</label>
              <input value={form.channel} onChange={e => set('channel', e.target.value)} placeholder="Email / Teams"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Drafted by</label>
              <input value={form.draftedBy} onChange={e => set('draftedBy', e.target.value)} placeholder="Name"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Approver</label>
              <input value={form.approver} onChange={e => set('approver', e.target.value)} placeholder="Name"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Send date <span className="text-red-500">*</span></label>
              <input type="date" value={form.sendDate} onChange={e => set('sendDate', e.target.value)}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">REACH stage</label>
              <select value={form.reachStage} onChange={e => set('reachStage', e.target.value as ReachStage)}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Approval status</label>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {(['pending', 'approved', 'rejected'] as ApprovalStatus[]).map(s => (
                <button key={s} type="button" onClick={() => set('approvalStatus', s)}
                  className={`flex-1 py-2 text-xs font-semibold transition-all ${
                    form.approvalStatus === s ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}>
                  {APPROVAL_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Optional context or links"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex justify-end gap-2">
          <button onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !form.item.trim() || !form.sendDate}
            className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60">
            <Check size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const CommsLogPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [items, setItems] = useState<ReachCommsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'new' | ReachCommsItem | null>(null);
  const [stageFilter, setStageFilter] = useState<ReachStage | 'all'>('all');

  useEffect(() => {
    if (!projectId) return;
    reachCommsAPI.list(projectId).then(r => {
      setItems(r.data.comms);
      setLoading(false);
    });
  }, [projectId]);

  const filtered = stageFilter === 'all' ? items : items.filter(i => i.reachStage === stageFilter);
  const sorted = [...filtered].sort((a, b) => a.sendDate.localeCompare(b.sendDate));

  const create = async (data: FormData) => {
    if (!projectId) return;
    const r = await reachCommsAPI.create(projectId, data);
    setItems(prev => [...prev, r.data.item]);
    setModal(null);
  };

  const update = async (id: string, data: FormData) => {
    if (!projectId) return;
    const r = await reachCommsAPI.update(projectId, id, data);
    setItems(prev => prev.map(i => i.id === id ? r.data.item : i));
    setModal(null);
  };

  const remove = async (id: string) => {
    if (!projectId || !window.confirm('Delete this comms item?')) return;
    await reachCommsAPI.delete(projectId, id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  if (!projectId) return null;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <MessageSquare size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Comms Log</h1>
            <p className="text-slate-500 text-sm font-medium">{items.length} communications planned</p>
          </div>
          <div className="ml-auto">
            <button onClick={() => setModal('new')}
              className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
              <Plus size={16} /> Add comms item
            </button>
          </div>
        </header>

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0] flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">Stage:</span>
            {(['all', ...STAGES] as const).map(s => (
              <button key={s} onClick={() => setStageFilter(s)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  stageFilter === s ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20 text-slate-400 text-sm">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <MessageSquare size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">No comms items yet.</p>
              <p className="text-slate-400 text-xs mt-1">Add planned communications to track drafting and approval.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Communication</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Audience</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Channel</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Send date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Stage</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {sorted.map(item => (
                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-navy text-sm leading-snug max-w-xs">{item.item}</p>
                        {item.notes && <p className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{item.notes}</p>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">{item.audience || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">{item.channel || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">{item.sendDate}</td>
                      <td className="px-4 py-3"><REACHStageBadge stage={item.reachStage} /></td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${APPROVAL_COLORS[item.approvalStatus]}`}>
                          {APPROVAL_LABELS[item.approvalStatus]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => setModal(item)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors">
                            <Pencil size={13} />
                          </button>
                          <button onClick={() => remove(item.id)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal === 'new' && (
        <FormModal onSave={create} onClose={() => setModal(null)} />
      )}
      {modal && modal !== 'new' && (
        <FormModal initial={modal} onSave={d => update(modal.id, d)} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

export default CommsLogPage;
